import { Avatar, Box, ListItemAvatar, Stack, Typography, useTheme } from '@mui/material';
import { IconHeadset } from '@shared/assets/images/svgs';
import { CallActors } from '@shared/constants/recipient-status.constants';
import {
  ConversationActorsType,
  ConversationLogDto,
} from '@shared/services/conversationApiService/apiService';
import { RootState, useAppSelector } from '@shared/store';
import React, { useRef } from 'react';
import { messageTimeCalculation } from './utils';
import { activeModeType } from '@shared/store/ducks/customizer/customizer.type';

interface ConversationListMessageProps {
  messageItem: ConversationLogDto;
  actorName?: string;
  isStacked?: boolean;
  timeSincePreviousMessage?: number;
  isActive?: boolean;
  isSeekable?: boolean;
  onActivate?: () => void;
}

export const ConversationListMessage: React.FC<ConversationListMessageProps> = ({
  messageItem,
  actorName,
  isStacked,
  timeSincePreviousMessage,
  isActive = false,
  isSeekable = false,
  onActivate,
}: ConversationListMessageProps) => {
  const theme = useTheme();
  const customizer = useAppSelector((state: RootState) => state.customizer);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { actor } = messageItem;
  const isAgent: boolean = actor === CallActors.AGENT;

  const calculateMessageBgColor = (actor: ConversationActorsType) => {
    // A supervisor takes the agent's place in the conversation, so their lines are
    // coloured like the agent's rather than the customer's - the label is what tells the
    // two apart.
    const isCustomerSide = actor === CallActors.CUSTOMER;

    if (customizer.activeMode == activeModeType.light) {
      return isCustomerSide ? theme.palette.extendedGrey?.neutralLight : 'white';
    } else {
      return isCustomerSide
        ? theme.palette.extendedGrey?.neutralLight
        : theme.palette.primary.light;
    }
  };

  const handleActivate = () => {
    if (!isSeekable) return;
    onActivate?.();
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Box
      ref={rootRef}
      display="flex"
      alignItems={isAgent ? 'flex-start' : 'flex-end'}
      flexDirection={isAgent ? 'row' : 'row-reverse'}
      role={isSeekable ? 'button' : undefined}
      tabIndex={isSeekable ? 0 : undefined}
      aria-current={isActive ? 'true' : undefined}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (isSeekable && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleActivate();
        }
      }}
      sx={{
        margin: `${isStacked ? 0 : '20px'} 0 4px ${isSeekable || isAgent ? 0 : '56px'}`,
        ...(isSeekable || isActive
          ? {
              cursor: isSeekable ? 'pointer' : 'default',
              borderRadius: 1,
              backgroundColor: isActive ? 'primary.light' : undefined,
              '&:hover': isSeekable
                ? { outline: `1px solid ${theme.palette.primary.main}` }
                : undefined,
            }
          : {}),
      }}>
      {isAgent && (
        <ListItemAvatar>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: theme.palette.secondary?.light,
            }}>
            <IconHeadset />
          </Avatar>
        </ListItemAvatar>
      )}
      <Box alignItems={isAgent ? 'flex-start' : 'flex-end'} display="flex" flexDirection="column">
        {!isStacked && (
          <Typography variant="body2" color={theme.palette.extendedGrey?.darker} mb={1}>
            {actorName ? actorName : messageItem.actor},
          </Typography>
        )}
        <Box
          mb={timeSincePreviousMessage !== undefined || !isStacked ? 0 : 1}
          sx={{
            p: 1,
            borderRadius: isAgent ? '0 12px 12px 12px' : '12px 0 12px 12px',
            backgroundColor: calculateMessageBgColor(actor),
            ml: isAgent ? 'unset' : 'auto',
            mr: isAgent ? 'auto' : 'unset',
            maxWidth: '320px',
          }}>
          {messageItem.message}
        </Box>
        <Stack direction="row" sx={{ mt: 0.5 }}>
          <Typography variant="body2" color={theme.palette.extendedGrey?.neutral}>
            {messageTimeCalculation(messageItem.createdAt)}
          </Typography>
          {timeSincePreviousMessage !== undefined && (
            <Typography variant="body2" color={theme.palette.extendedGrey?.neutral} ml={1}>
              ( +{timeSincePreviousMessage} sec )
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
